import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Textarea } from '@/components/shadcn/textarea'
import { Switch } from '@/components/shadcn/switch'

import { FileUpload } from '@/ui-legacy/Forms/FileUpload'
import { ENDPOINTS } from '@/util'
import DOMPurify from 'isomorphic-dompurify'
import { useSessionStorage } from '@/hooks/useSessionStorage'
import { useCurrentApplication } from '../useCurrentApplication'
import { toast } from 'sonner'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

// REST API types
type MessagesEntityTypesUpdateEntityType = RestSchemas['Messages.EntityTypes.UpdateEntityType']
type MessagesTemplatesFileTemplate = RestSchemas['Messages.Templates.FileTemplate']
type MessagesTemplatesNewTemplate = RestSchemas['Messages.Templates.NewTemplate']
type CommonStorageFileType = RestSchemas['Common.StorageFileType']

// Zod validation schema
const formSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name cannot exceed 100 characters'),
  description: z.string().min(1, 'Description is required').max(200, 'Description cannot exceed 200 characters'),
  key: z.string().max(200, 'Key cannot exceed 200 characters'),
  requireApprovals: z.boolean(),
})

export type TFileTemplateForm = z.infer<typeof formSchema>

// Props interface
type FileTemplateFormV2Props = {
  // applicationId: number
  selectedEntityType?: MessagesEntityTypesUpdateEntityType
  selectedTemplate?: MessagesTemplatesFileTemplate
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  invalidateTemplates?: () => void
}

export const FileTemplateFormV2 = (props: FileTemplateFormV2Props) => {
  const [sheetOpen, setSheetOpen] = useState(false)
  // const edit = !!props.selectedTemplate
  const { applicationId } = useCurrentApplication()

  const { selectedEntityType, selectedTemplate, onSubmit, invalidateTemplates, sheetTrigger } = props

  // REST API client
  const { putTemplatesApiAddTemplate, postTemplatesApiUpdateTemplate, getTemplatesApiListFileTemplates } =
    useRestClient()

  // Update mutation
  const { mutate: updateTemplate, isPending: isUpdating } = postTemplatesApiUpdateTemplate.useMutation(undefined, {
    onSuccess: () => {
      getTemplatesApiListFileTemplates.invalidateQueries({})
      if (sheetTrigger) {
        setSheetOpen(false)
      }
      toast.success('Template successfully updated')
      onSubmit?.()
      invalidateTemplates?.()
    },
    onError: () => {
      toast.error('Error updating template')
    },
  })

  // Create mutation
  const { mutate: addTemplate, isPending: isAdding } = putTemplatesApiAddTemplate.useMutation(undefined, {
    onSuccess: () => {
      getTemplatesApiListFileTemplates.invalidateQueries({})
      if (sheetTrigger) {
        setSheetOpen(false)
      }
      toast.success('Template successfully added')
      onSubmit?.()
      invalidateTemplates?.()
    },
    onError: () => {
      toast.error('Error adding template')
    },
  })

  // Submit handler
  const { id: entityTypeId } = selectedEntityType ?? {}
  const submitHandler = useCallback(
    (data: TFileTemplateForm) => {
      if (selectedTemplate) {
        const updatedTemplate: MessagesTemplatesFileTemplate = {
          ...selectedTemplate,
          ...data,
        }
        updateTemplate({ body: updatedTemplate })
      } else {
        const newTemplate: MessagesTemplatesNewTemplate = {
          entityTypeId: entityTypeId!,
          applicationId,
          ...data,
        }
        addTemplate({ body: newTemplate })
      }
    },
    [addTemplate, applicationId, entityTypeId, selectedTemplate, updateTemplate]
  )

  const isLoading = isUpdating || isAdding

  if (isLoading) return <FormComponentLoading />

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} submitHandler={submitHandler} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} submitHandler={submitHandler} />
  )
}

const FormComponent = (
  props: FileTemplateFormV2Props & {
    submitHandler: (data: TFileTemplateForm) => void
  }
) => {
  const [token] = useSessionStorage('token', '')
  const selectedTemplate: Partial<MessagesTemplatesFileTemplate> = props.selectedTemplate ?? {}
  const { name, description, key, requireApprovals } = selectedTemplate
  const { applicationId } = useCurrentApplication()

  const defaultValues = useMemo(() => {
    return {
      name: name ?? '',
      description: description ?? '',
      key: key ?? '',
      requireApprovals: requireApprovals ?? true,
    }
  }, [name, description, key, requireApprovals])

  const form = useForm<TFileTemplateForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const onDownloadClick = async () => {
    if (!props.selectedTemplate) return

    const fileType: CommonStorageFileType = 1

    try {
      const response = await fetch(
        `${ENDPOINTS.LANDING}/storage/download_file/${props.selectedTemplate.templateFileUrl},${applicationId},${0},${fileType}`,
        {
          headers: { Authorization: 'Bearer ' + token },
        }
      )
      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.setAttribute('href', url)
        a.setAttribute('download', props.selectedTemplate.templateFilename ?? 'filename.txt')
        document.body.appendChild(a)
        a.click()
        a.remove()
      } else {
        let errMessage = 'Unknown error'
        if (response.statusText) errMessage = response.statusText
        try {
          const tryJson = await response.json()
          if (tryJson.Message) errMessage = tryJson.Message
        } catch (error) {
          // ignore
        }
        toast.error('Error downloading file')
      }
    } catch (error) {
      toast.error(`Error downloading file: ${DOMPurify.sanitize(`${error}`)}`)
      console.error(DOMPurify.sanitize(`${error}`))
    }
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(props.submitHandler)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              {/* Name Field */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Description Field */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={3} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Key Field */}
              <FormField
                control={form.control}
                name="key"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Key</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Require Approvals Switch */}
              <FormField
                control={form.control}
                name="requireApprovals"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Require Approvals</FormLabel>
                      <div className="text-sm text-muted-foreground">Enable approval workflow for this template</div>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* File Upload Section - Only show for existing templates */}
              {props.selectedTemplate && (
                <>
                  <div className="space-y-4">
                    <div>
                      <FormLabel className="text-base">Template File</FormLabel>
                      <div className="mb-3 text-sm text-muted-foreground">Upload an example file for this template</div>
                      <FileUpload
                        onSubmit={() => {
                          if (props.invalidateTemplates) props.invalidateTemplates()
                        }}
                        applicationId={applicationId}
                        templateId={props.selectedTemplate.id}
                        projectId={0}
                        entityId={0}
                      />
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">Current example: </span>
                      {props.selectedTemplate.templateFilename ? (
                        <button
                          type="button"
                          onClick={onDownloadClick}
                          className="cursor-pointer text-blue-600 underline hover:text-blue-800"
                        >
                          {props.selectedTemplate.templateFilename}
                        </button>
                      ) : (
                        <span className="text-muted-foreground">none</span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="form-action-container">
              {selectedTemplate === undefined ? (
                <Button type="submit">Update Template</Button>
              ) : (
                <Button type="submit">Create Template</Button>
              )}
            </div>
          </div>
        </form>
      </Form>
    </>
  )
}
